import {
  IconAddressBook,
  IconAward,
  IconBell,
  IconBellCog,
  IconBooks,
  IconBuilding,
  IconCash,
  IconCertificate,
  IconChartBar,
  IconChartHistogram,
  IconCoins,
  IconCreditCard,
  IconCube,
  IconDiamond,
  IconFileDescription,
  IconFileTextSpark,
  IconGift,
  IconKey,
  IconLayoutDashboard,
  IconLink,
  IconLockAccess,
  IconMail,
  IconMailForward,
  IconMessage2,
  IconNetwork,
  IconPalette,
  IconPhoto,
  IconPlugConnected,
  IconReceipt,
  IconRosetteDiscountCheck,
  IconSettings,
  IconShield,
  IconShieldCheck,
  IconShieldLock,
  IconSignature,
  IconSmartHome,
  IconSparkles,
  IconTicket,
  IconUser,
  IconUsers,
  IconWebhook,
  IconWorld,
} from '@tabler/icons-react'

import {
  CreateContactIcon,
  CreateEnvelopeIcon,
  CreateTemplateIcon,
  CreateVaultIcon,
} from '@/components/common/create-icon'
import type { PlanType } from '@/lib/plans'
import { PLANS } from '@/lib/plans'
import {
  reportSearchKey,
  reportsMenuConfig,
} from '@/pages/reports/reports-menu'

export interface SearchablePageItem {
  translationKey: string
  path: string
  icon: React.ComponentType<{ className?: string }>
  /** Digit pressed with Cmd/Ctrl to jump to this page; rendered per platform. */
  shortcutKey?: string
  requiresAdmin?: boolean
  requiresAdminAndNotManaged?: boolean
  /** Out of reach while managing someone else's account: the manager's own
   *  applies to it, so there is nothing of its own to configure. */
  hiddenWhileManaging?: boolean
  requiresNfe?: boolean
  /**
   * Gated on the affiliate program being visible to this session (build flag +
   * admin + not-managed + not-managing + not-blocked). Resolved by
   * `useAffiliateEntry` in `useFilteredPages`, since it can't be expressed with
   * the static admin/managed flags above.
   */
  requiresAffiliateProgram?: boolean
  minimumPlan?: PlanType
}

export interface QuickActionItem {
  translationKey: string
  path: string
  icon: React.ComponentType<{ className?: string }>
  /**
   * Minimum plan the action's destination requires. The action stays listed
   * for retail accounts without it (the destination shows the upsell) and is
   * hidden for managed accounts, which can't act on a paywall.
   */
  minimumPlan?: PlanType
}

export const searchablePages: Array<SearchablePageItem> = [
  // Main navigation
  {
    translationKey: 'pages.home',
    path: '/',
    icon: IconSmartHome,
    shortcutKey: '1',
  },
  {
    translationKey: 'pages.vaults',
    path: '/vaults',
    icon: IconShieldLock,
    shortcutKey: '2',
  },
  {
    translationKey: 'pages.envelopes',
    path: '/envelopes',
    icon: IconMail,
    shortcutKey: '3',
  },
  {
    translationKey: 'pages.templates',
    path: '/templates',
    icon: IconFileTextSpark,
    shortcutKey: '4',
  },
  {
    translationKey: 'pages.contacts',
    path: '/contacts',
    icon: IconAddressBook,
    shortcutKey: '5',
  },
  {
    translationKey: 'pages.reports',
    path: '/reports',
    icon: IconChartBar,
    shortcutKey: '6',
  },
  ...reportsMenuConfig.flatMap((section) =>
    section.items.map((item) => ({
      translationKey: reportSearchKey(item.id),
      path: item.path,
      icon: item.icon,
    })),
  ),
  {
    translationKey: 'pages.settings',
    path: '/settings',
    icon: IconSettings,
    shortcutKey: '7',
  },

  // Settings - Profile (all users)
  {
    translationKey: 'pages.profileGeneral',
    path: '/settings/profile/general',
    icon: IconUser,
  },
  {
    translationKey: 'pages.profileNotifications',
    path: '/settings/profile/notifications',
    icon: IconBell,
  },
  {
    translationKey: 'pages.profilePendingInvites',
    path: '/settings/profile/pending-invites',
    icon: IconMailForward,
  },
  {
    translationKey: 'pages.profileSecurity',
    path: '/settings/profile/security',
    icon: IconShield,
  },
  {
    translationKey: 'pages.profileSignatures',
    path: '/settings/profile/signatures',
    icon: IconSignature,
  },
  {
    translationKey: 'pages.profileDigitalCertificates',
    path: '/settings/profile/digital-certificates',
    icon: IconCertificate,
  },
  {
    translationKey: 'pages.profileApiTokens',
    path: '/settings/profile/api-tokens',
    icon: IconKey,
    minimumPlan: PLANS.PRO,
  },
  {
    translationKey: 'pages.profileMcp',
    path: '/settings/profile/mcp',
    icon: IconPlugConnected,
    minimumPlan: PLANS.PRO,
  },

  // Settings - Account (admin only)
  {
    translationKey: 'pages.accountGeneral',
    path: '/settings/account/general',
    icon: IconBuilding,
    requiresAdmin: true,
  },
  {
    translationKey: 'pages.accountNotifications',
    path: '/settings/account/notifications',
    icon: IconBellCog,
    requiresAdmin: true,
  },
  {
    translationKey: 'pages.accountAccessPolicy',
    path: '/settings/account/access-policy',
    icon: IconLockAccess,
    requiresAdmin: true,
    minimumPlan: PLANS.ONE,
  },
  {
    translationKey: 'pages.accountSustainabilityBadge',
    path: '/settings/account/sustainability-badge',
    icon: IconAward,
    requiresAdmin: true,
  },
  {
    translationKey: 'pages.accountSso',
    path: '/settings/account/sso',
    icon: IconShieldLock,
    requiresAdmin: true,
    minimumPlan: PLANS.MAX,
  },
  {
    translationKey: 'pages.accountBlockchain',
    path: '/settings/account/blockchain',
    icon: IconCube,
    requiresAdmin: true,
    minimumPlan: PLANS.MAX,
  },
  {
    translationKey: 'pages.accountAiAssistant',
    path: '/settings/account/ai-assistant',
    icon: IconSparkles,
    requiresAdmin: true,
    minimumPlan: PLANS.PRO,
  },
  {
    translationKey: 'pages.accountWebhooks',
    path: '/settings/account/webhooks',
    icon: IconWebhook,
    requiresAdmin: true,
    minimumPlan: PLANS.PRO,
  },
  {
    translationKey: 'pages.accountUsers',
    path: '/settings/account/users',
    icon: IconUsers,
    requiresAdmin: true,
    minimumPlan: PLANS.ONE,
  },

  // Settings - Account (admin + not managed)
  {
    translationKey: 'pages.accountManagedAccounts',
    path: '/settings/account/managed-accounts',
    icon: IconNetwork,
    requiresAdminAndNotManaged: true,
  },

  // Settings - White label (admin + not managed: the manager's applies)
  {
    translationKey: 'pages.whiteLabelGeneral',
    path: '/settings/white-label/general',
    icon: IconPalette,
    requiresAdminAndNotManaged: true,
    hiddenWhileManaging: true,
  },
  {
    translationKey: 'pages.whiteLabelLogos',
    path: '/settings/white-label/logos',
    icon: IconPhoto,
    requiresAdminAndNotManaged: true,
    hiddenWhileManaging: true,
  },
  {
    translationKey: 'pages.whiteLabelMessagingChannels',
    path: '/settings/white-label/messaging-channels',
    icon: IconMessage2,
    requiresAdminAndNotManaged: true,
    hiddenWhileManaging: true,
  },
  {
    translationKey: 'pages.whiteLabelCaptcha',
    path: '/settings/white-label/captcha',
    icon: IconShieldCheck,
    requiresAdminAndNotManaged: true,
    hiddenWhileManaging: true,
  },
  {
    translationKey: 'pages.whiteLabelDomain',
    path: '/settings/white-label/domain',
    icon: IconWorld,
    requiresAdminAndNotManaged: true,
    hiddenWhileManaging: true,
  },
  {
    translationKey: 'pages.whiteLabelSeals',
    path: '/settings/white-label/seals',
    icon: IconRosetteDiscountCheck,
    requiresAdminAndNotManaged: true,
    hiddenWhileManaging: true,
  },
  // Settings - Billing (admin + not managed)
  {
    translationKey: 'pages.billingPlans',
    path: '/settings/billing/plans',
    icon: IconDiamond,
    requiresAdminAndNotManaged: true,
  },
  {
    translationKey: 'pages.billingInvoices',
    path: '/settings/billing/invoices',
    icon: IconReceipt,
    requiresAdminAndNotManaged: true,
  },
  {
    translationKey: 'pages.billingTaxData',
    path: '/settings/billing/tax-data',
    icon: IconFileDescription,
    requiresAdminAndNotManaged: true,
    requiresNfe: true,
  },

  // Affiliate program (admins of a non-managed, non-blocked account). Indexed
  // page by page like Reports and Settings: a section whose root is its only
  // searchable entry is nine pages nobody can reach from here.
  {
    translationKey: 'pages.affiliateProgram',
    path: '/affiliate',
    icon: IconGift,
    shortcutKey: '8',
    requiresAffiliateProgram: true,
  },
  {
    translationKey: 'pages.affiliateOverview',
    path: '/affiliate/overview',
    icon: IconLayoutDashboard,
    requiresAffiliateProgram: true,
  },
  {
    translationKey: 'pages.affiliateAnalytics',
    path: '/affiliate/analytics',
    icon: IconChartHistogram,
    requiresAffiliateProgram: true,
  },
  {
    translationKey: 'pages.affiliateCustomers',
    path: '/affiliate/customers',
    icon: IconUsers,
    requiresAffiliateProgram: true,
  },
  {
    translationKey: 'pages.affiliateLinks',
    path: '/affiliate/links',
    icon: IconLink,
    requiresAffiliateProgram: true,
  },
  {
    translationKey: 'pages.affiliateCoupons',
    path: '/affiliate/coupons',
    icon: IconTicket,
    requiresAffiliateProgram: true,
  },
  {
    translationKey: 'pages.affiliateMaterials',
    path: '/affiliate/materials',
    icon: IconBooks,
    requiresAffiliateProgram: true,
  },
  {
    translationKey: 'pages.affiliateCommissions',
    path: '/affiliate/commissions',
    icon: IconCoins,
    requiresAffiliateProgram: true,
  },
  {
    translationKey: 'pages.affiliateWithdrawals',
    path: '/affiliate/withdrawals',
    icon: IconCash,
    requiresAffiliateProgram: true,
  },
  {
    translationKey: 'pages.affiliatePayoutMethods',
    path: '/affiliate/payout-methods',
    icon: IconCreditCard,
    requiresAffiliateProgram: true,
  },
]

export const quickActions: Array<QuickActionItem> = [
  {
    translationKey: 'actions.createContact',
    path: '/contacts?trigger=create',
    icon: CreateContactIcon,
  },
  {
    translationKey: 'actions.createEnvelope',
    path: '/envelope?source=search_dialog',
    icon: CreateEnvelopeIcon,
  },
  {
    translationKey: 'actions.createTemplate',
    path: '/template',
    icon: CreateTemplateIcon,
    minimumPlan: PLANS.ONE,
  },
  {
    translationKey: 'actions.createVault',
    path: '/vaults?trigger=create',
    icon: CreateVaultIcon,
  },
]
